import React from "react";
import { Box, Grid2, Typography } from "@mui/material";
import TileType, { TileTypes } from "../../../types/tileType";
import FormAutocomplete from "../../forms/FormAutocomplete";
import FormTextField from "../../forms/FormTextField";
import { Control, UseFormSetValue } from "react-hook-form";
import Tile from "../../../types/tile";
import Option from "../../../types/option";
import ConnectionType from "../../../types/connectionType";
import DataType from "../../../types/dataType";
import Connection from "../../../types/connection";

interface Props {
  tileType: "dashboard" | "tile";
  control: Control<Tile, any>;
  dashboardOptions: Option[] | undefined;
  connectionTypes: ConnectionType[] | undefined;
  connectionList: Connection[] | undefined;
  formValues: Tile;
  dataTypeList: DataType[] | undefined;
  tileTypeList: TileType[] | undefined;
  backgrounds: Option[] | undefined;
  icons: Option[] | undefined;
  setValue: UseFormSetValue<Tile>;
  loadWithSkeleton: (
    element: React.ReactNode
  ) =>
    | string
    | number
    | boolean
    | Iterable<React.ReactNode>
    | JSX.Element
    | null
    | undefined;
}

const BasicInfo: React.FC<Props> = (props) => {
  const {
    tileType,
    control,
    dashboardOptions,
    connectionTypes,
    connectionList,
    formValues,
    dataTypeList,
    tileTypeList,
    backgrounds,
    icons,
    setValue,
    loadWithSkeleton,
  } = props;
  return (
    <>
      <Box display="flex" flexDirection="row" width="100%">
        <Typography fontWeight="bold">
          {tileType === "tile" ? "Basic Information" : "New Dashboard Details"}
        </Typography>
      </Box>
      <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
        {loadWithSkeleton(
          <FormTextField
            control={control}
            fieldName="title"
            label="Name"
            required
          />
        )}
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
        {loadWithSkeleton(
          <FormAutocomplete
            control={control}
            fieldName="dashboard"
            label="Parent Dashboard"
            required
            options={dashboardOptions || []}
            multiple={false}
            staticFilter
          />
        )}
      </Grid2>
      {tileType === "tile" && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormAutocomplete
              control={control}
              fieldName="connectionType"
              label="Connection Type"
              required
              options={connectionTypes || []}
              multiple={false}
              staticFilter
            />
          )}
        </Grid2>
      )}
      {tileType === "tile" && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormAutocomplete
              control={control}
              fieldName="connections"
              label="Connection"
              required
              options={connectionList || []}
              multiple
              noOptionsText={
                !!formValues.connectionType
                  ? "No connections available"
                  : "Select connection type to load connections"
              }
              defaultValue={
                !!formValues?.connections?.length ? formValues.connections : []
              }
              staticFilter
            />
          )}
        </Grid2>
      )}
      {tileType === "tile" && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormAutocomplete
              control={control}
              fieldName="dataType"
              label="Data Type"
              required
              multiple={false}
              options={dataTypeList || []}
              noOptionsText={
                !!formValues.connectionType
                  ? "No data types available"
                  : "Select connection type to load available data types"
              }
              staticFilter
            />
          )}
        </Grid2>
      )}
      <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
        {loadWithSkeleton(
          <FormAutocomplete
            control={control}
            fieldName="type"
            label="Tile Type"
            required
            multiple={false}
            options={tileTypeList || []}
            disabled={tileType === "dashboard"}
            noOptionsText={
              !!formValues.dataType
                ? "No tile types available"
                : "Select data type to load available tile types"
            }
            staticFilter
          />
        )}
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
        {loadWithSkeleton(
          <FormTextField
            control={control}
            fieldName="order"
            label="Tile Order"
            type="number"
          />
        )}
      </Grid2>
      {tileType === "dashboard" && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormAutocomplete
              control={control}
              fieldName="backgroundId"
              label="Background Image"
              multiple={false}
              disabled={!formValues.type?.hasBackground}
              required={!!formValues?.type?.hasBackground}
              defaultValue={
                !!backgrounds
                  ? backgrounds.find((x) => x.id === formValues?.backgroundId)
                  : null
              }
              options={
                !!formValues?.type?.hasBackground ? backgrounds || [] : []
              }
              noOptionsText={
                !!formValues.type
                  ? "No backgrounds available"
                  : "Select tile type to load available backgrounds"
              }
              onChange={(e) => setValue("backgroundId", e?.id!)}
              staticFilter
            />
          )}
        </Grid2>
      )}
      {tileType === "tile" && !!formValues?.type?.hasIcon && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormAutocomplete
              control={control}
              fieldName="tileIconType"
              label="Icon"
              multiple={false}
              disabled={!formValues.type?.hasIcon}
              required={!!formValues?.type?.hasIcon}
              options={!!formValues?.type?.hasIcon ? icons || [] : []}
              noOptionsText={
                !!formValues.type
                  ? "No icons available"
                  : "Select tile type to load available icons"
              }
              staticFilter
            />
          )}
        </Grid2>
      )}
      {tileType === "tile" && formValues.type?.id === TileTypes.Number && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormTextField
              control={control}
              fieldName="value"
              label="Number Value"
              type="number"
              required={formValues.type?.id === TileTypes.Number}
              disabled={formValues.type?.id !== TileTypes.Number}
            />
          )}
        </Grid2>
      )}
      {tileType === "tile" && !!formValues?.type?.hasReport && (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          {loadWithSkeleton(
            <FormAutocomplete
              control={control}
              fieldName="reportType"
              label="Report Type"
              multiple={false}
              disabled={!formValues.type?.hasReport}
              required={!!formValues?.type?.hasReport}
              options={
                !!formValues?.type?.hasReport
                  ? formValues?.dataType?.supportedReportTypes || []
                  : []
              }
              noOptionsText={
                !!formValues.dataType && !!formValues.type
                  ? "No report types available"
                  : "Select data type and tile type to load available reports"
              }
              staticFilter
            />
          )}
        </Grid2>
      )}
    </>
  );
};

export default BasicInfo;
